import type { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@src/design-system/badge';
import { Text } from '@src/design-system/text';
import { ReminderChannelCell } from '@src/features/reminders/components/all-reminders/shared/reminder-channel-cell';
import {
  getStatusBadgeVariant,
  getTemplateStatusBadgeClassName,
} from '@src/shared/features/reminders/helpers/get-status-badge-variant';
import { cn } from '@src/design-system/helpers';
import { REMINDER_CHANNELS } from '@src/shared/types/reminder';
import {
  getEffectiveTemplateStatus,
  isAlwaysApprovedChannel,
} from '@src/shared/features/reminders/helpers/template-approval';
import { type TReminderTemplate } from '@src/shared/types/reminder-template';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { stripHtml } from '@src/shared/utils/html-utils';
import { createAlphabeticalSortingFunction } from '@src/design-system/molecules/ticlick-table/table-cells/utils/sorting';

export const reminderTemplatesColumns: ColumnDef<TReminderTemplate>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Template Name',
    sortingFn: createAlphabeticalSortingFunction('name'),
    meta: { isCopy: true },
    cell: ({ row }) => <span>{formatTextForDisplay(row.original.name)}</span>,
  },
  {
    id: 'description',
    accessorKey: 'content',
    header: 'Description',
    cell: ({ row }) => {
      const description = stripHtml(row.original.content || '').trim();
      return <span className='max-w-[220px]'>{description || '—'}</span>;
    },
  },
  {
    id: 'channel',
    accessorKey: 'channel',
    header: 'Channel',
    cell: ({ row }) => <ReminderChannelCell channel={row.original.channel} />,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const { channel, status } = row.original;
      const isWhatsApp = channel === REMINDER_CHANNELS.whatsapp;

      if (!isWhatsApp && !isAlwaysApprovedChannel(channel)) {
        return <Text className='text-gray-600'>—</Text>;
      }

      const displayStatus = getEffectiveTemplateStatus({ channel, status });

      const templateStatusClassName =
        getTemplateStatusBadgeClassName(displayStatus);

      return (
        <Badge
          variant={
            templateStatusClassName
              ? 'outline'
              : getStatusBadgeVariant(displayStatus)
          }
          className={cn(
            'py-1.5 px-2 rounded-[8px] font-medium',
            templateStatusClassName
          )}
        >
          {formatTextForDisplay(displayStatus)}
        </Badge>
      );
    },
  },
];
